import React, { useEffect } from 'react';
import { Box, Drawer, Button, Stack, TextField, FormControl, InputLabel, Select, MenuItem, IconButton, DialogTitle, Checkbox, ListItemText } from '@mui/material';
import { useFormik } from 'formik';
import { useSearchParams } from 'react-router-dom';
import { Close } from '@mui/icons-material';

const MULTI_FILTER_KEYS = ['functions', 'customs', 'additionals'];

const CLUB_FUNCTION_OPTIONS = [
    { value: 'cupons', label: 'Rede de descontos' },
    { value: 'vouchers', label: 'Rede local' },
    { value: 'cashback', label: 'Cashback' },
    { value: 'reseller', label: 'Revenda/Página de vendas' },
    { value: 'benefit', label: 'Gestão de benefícios' },
    { value: 'telepet', label: 'Telepet' },
];

const CLUB_CUSTOM_OPTIONS = [
    { value: 'custom_app', label: 'Aplicativo' },
    { value: 'custom_telemedicina', label: 'Telemedicina' },
    { value: 'custom_telepet', label: 'Telepet' },
];

const CLUB_ADDITIONAL_OPTIONS = [
    { value: 'telemedicina', label: 'Telemedicina' },
    { value: 'telepsicologia', label: 'Telepsicologia' },
    { value: 'seguro', label: 'Seguro de vida' },
    { value: 'telepet', label: 'Telepet' },
];

const SIGNATURE_STATUS_OPTIONS = [
    { value: 'ACTIVE', label: 'Ativa' },
    { value: 'WAITING', label: 'Aguardando' },
    { value: 'EXPIRED', label: 'Vencida' },
    { value: 'CANCELLED', label: 'Cancelada' },
    { value: 'INACTIVE', label: 'Inativa' },
    { value: 'NONE', label: 'Sem assinatura' },
];

const CONTRACT_STATUS_OPTIONS = [
    { value: 'ANY', label: 'Com contrato' },
    { value: 'NONE', label: 'Sem contrato' },
    { value: 'UNSIGNED', label: 'Aguardando assinatura' },
    { value: 'SIGNED', label: 'Assinado (aguardando conclusão)' },
    { value: 'CONCLUDED', label: 'Concluído' },
    { value: 'CANCELED', label: 'Cancelado' },
];

const DOMAIN_STATUS_OPTIONS = [
    { value: 'active', label: 'Ativo' },
    { value: 'processing', label: 'Verificando DNS' },
    { value: 'provisioning_hostname', label: 'Emitindo certificado' },
    { value: 'provisioning_route', label: 'Conectando ao clube' },
    { value: 'verifying', label: 'Finalizando ativação' },
    { value: 'removing', label: 'Removendo domínio' },
    { value: 'failed_cname', label: 'CNAME ausente ou incorreto' },
    { value: 'failed', label: 'Não foi possível concluir' },
    { value: 'unknown', label: 'Sem status' },
    { value: 'none', label: 'Sem domínio' },
];

function valuesFromSearchParams(searchParams) {
    return {
        search: searchParams.get('search') || '',
        functions: searchParams.getAll('functions'),
        customs: searchParams.getAll('customs'),
        additionals: searchParams.getAll('additionals'),
        status: searchParams.get('status') || '',
        signatureStatus: searchParams.get('signatureStatus') || '',
        domainStatus: searchParams.get('domainStatus') || '',
        contractStatus: searchParams.get('contractStatus') || '',
    };
}

function MultiFilterSelect({ id, name, label, value, onChange, options }) {
    return (
        <FormControl fullWidth size="small" margin="dense">
            <InputLabel id={`${id}-label`} shrink>{label}</InputLabel>
            <Select
                labelId={`${id}-label`}
                id={id}
                name={name}
                multiple
                value={value}
                onChange={onChange}
                label={label}
                notched
                displayEmpty
                renderValue={(selected) =>
                    selected.length === 0
                        ? 'Todas'
                        : selected
                            .map((item) => options.find((option) => option.value === item)?.label || item)
                            .join(', ')
                }
                MenuProps={{ PaperProps: { style: { maxHeight: 320 } } }}
            >
                {options.map(({ value: optionValue, label: optionLabel }) => (
                    <MenuItem key={optionValue} value={optionValue}>
                        <Checkbox checked={value.includes(optionValue)} size="small" sx={{ mr: 0.5, p: 0.25 }} />
                        <ListItemText primary={optionLabel} primaryTypographyProps={{ variant: 'body2' }} />
                    </MenuItem>
                ))}
            </Select>
        </FormControl>
    );
}

export function SearchDrawerClubs({ open, onClose, onClearFilters }) {
    const [searchParams, setSearchParams] = useSearchParams();

    const { values, handleSubmit, handleChange, resetForm } = useFormik({
        initialValues: valuesFromSearchParams(searchParams),
        onSubmit: (formValues) => {
            const filteredParams = {};
            for (const [key, value] of Object.entries(formValues)) {
                if (MULTI_FILTER_KEYS.includes(key)) {
                    if (Array.isArray(value) && value.length > 0) {
                        filteredParams[key] = value;
                    }
                    continue;
                }
                if (value !== '' && value !== null) {
                    filteredParams[key] = value;
                }
            }
            if (formValues.status) {
                filteredParams.status = formValues.status === 'true';
            }
            setSearchParams(filteredParams);
            onClose();
        }
    });

    useEffect(() => {
        resetForm({ values: valuesFromSearchParams(searchParams) });
    }, [searchParams, resetForm]);

    const handleClearFilters = () => {
        onClearFilters();
        setSearchParams({});
        resetForm();
    };

    const hasFilters = Object.entries(values).some(([key, value]) => {
        if (MULTI_FILTER_KEYS.includes(key)) return Array.isArray(value) && value.length > 0;
        return value !== '' && value !== null;
    });

    return (
        <Drawer anchor="right" open={open} onClose={onClose}>
            <Box sx={{ padding: 2, width: 350 }}>
                <DialogTitle sx={{ padding: 0 }}>
                    <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 3 }}>
                        Buscar clubes
                        <IconButton size="small" onClick={onClose}>
                            <Close />
                        </IconButton>
                    </Stack>
                </DialogTitle>
                <form onSubmit={handleSubmit}>
                    <Stack spacing={2}>
                        <TextField
                            fullWidth
                            label="Buscar"
                            variant="outlined"
                            name="search"
                            value={values.search}
                            onChange={handleChange}
                            placeholder="ID, nome, documento, responsável ou e-mail"
                            title="ID, nome, documento, responsável ou e-mail"
                        />
                        <FormControl fullWidth size="small" margin="dense">
                            <InputLabel id="status-label" shrink>Status</InputLabel>
                            <Select labelId="status-label" id="status" name="status" value={values.status} onChange={handleChange} label="Status" notched displayEmpty>
                                <MenuItem value="">Todos</MenuItem>
                                <MenuItem value="true">Ativo</MenuItem>
                                <MenuItem value="false">Inativo</MenuItem>
                            </Select>
                        </FormControl>
                        <FormControl fullWidth size="small" margin="dense">
                            <InputLabel id="signatureStatus-label" shrink>Status da assinatura</InputLabel>
                            <Select
                                labelId="signatureStatus-label"
                                id="signatureStatus"
                                name="signatureStatus"
                                value={values.signatureStatus}
                                onChange={handleChange}
                                label="Status da assinatura"
                                notched
                                displayEmpty
                            >
                                <MenuItem value="">Todos</MenuItem>
                                {SIGNATURE_STATUS_OPTIONS.map(({ value, label }) => (
                                    <MenuItem key={value} value={value}>{label}</MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                        <FormControl fullWidth size="small" margin="dense">
                            <InputLabel id="contractStatus-label" shrink>Contrato</InputLabel>
                            <Select
                                labelId="contractStatus-label"
                                id="contractStatus"
                                name="contractStatus"
                                value={values.contractStatus}
                                onChange={handleChange}
                                label="Contrato"
                                notched
                                displayEmpty
                            >
                                <MenuItem value="">Todos</MenuItem>
                                {CONTRACT_STATUS_OPTIONS.map(({ value, label }) => (
                                    <MenuItem key={value} value={value}>{label}</MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                        <FormControl fullWidth size="small" margin="dense">
                            <InputLabel id="domainStatus-label" shrink>Status do domínio</InputLabel>
                            <Select
                                labelId="domainStatus-label"
                                id="domainStatus"
                                name="domainStatus"
                                value={values.domainStatus}
                                onChange={handleChange}
                                label="Status do domínio"
                                notched
                                displayEmpty
                            >
                                <MenuItem value="">Todos</MenuItem>
                                {DOMAIN_STATUS_OPTIONS.map(({ value, label }) => (
                                    <MenuItem key={value} value={value}>{label}</MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                        <MultiFilterSelect
                            id="functions"
                            name="functions"
                            label="Funcionalidades"
                            value={values.functions}
                            onChange={handleChange}
                            options={CLUB_FUNCTION_OPTIONS}
                        />
                        <MultiFilterSelect
                            id="customs"
                            name="customs"
                            label="Customizações"
                            value={values.customs}
                            onChange={handleChange}
                            options={CLUB_CUSTOM_OPTIONS}
                        />
                        <MultiFilterSelect
                            id="additionals"
                            name="additionals"
                            label="Adicionais"
                            value={values.additionals}
                            onChange={handleChange}
                            options={CLUB_ADDITIONAL_OPTIONS}
                        />
                    </Stack>
                    <Stack direction="row" spacing={2} sx={{ mt: 2 }}>
                        <Button variant="contained" type="submit">
                            Buscar
                        </Button>
                        {hasFilters && (
                            <Button variant="outlined" onClick={handleClearFilters}>
                                Limpar Filtros
                            </Button>
                        )}
                    </Stack>
                </form>
            </Box>
        </Drawer>
    );
}
